import { useTranslation } from 'react-i18next';
import ErisResultsTable from './ErisStatusResultsTable';
import { ChangeEvent, useState } from 'react';
import { useErisInformationStore } from 'src/api/eris/store/ErisInformationStore';
import { ElevatorStatus, resolveElevatorTitle } from 'src/api/eris/models/StatusModel';
import Win95Button from '../../../common/button/Win95Button';
import useGroupService from 'src/api/eris/service/ErisInformationService';
import useDisasterService from 'src/api/eris/service/ErisDisasterService';
import DropdownSelect from '../../../common/select/DropdownSelect';
import { FormRow } from '../../../common/form/FormRow';

const ErisStatusResultsComponent = () => {
  const { t } = useTranslation();
  const { patchStatusPagination } = useErisInformationStore();
  const { pageSize, searchParams } = useErisInformationStore(
    (state) => state.statusSearchingParameters
  );
  const { searchGroupStatus } = useGroupService();
  const { getLatestDisaster } = useDisasterService();

  const [inputBuildingName, setInputBuildingName] = useState<string>(
    searchParams.buildingName ?? ''
  );
  const [inputOperationStatus, setInputOperationStatus] = useState<ElevatorStatus | undefined>(
    searchParams.operationStatus
  );

  const handleOnBuildingNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setInputBuildingName(event.target.value);
  };

  const handleOnTargetChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value);
    if (value === -1) {
      setInputOperationStatus(undefined);
    } else {
      setInputOperationStatus(value as ElevatorStatus);
    }
  };

  const handleOnSearch = () => {
    const committedSearchParams = {
      ...searchParams,
      buildingName: inputBuildingName,
      operationStatus: inputOperationStatus,
    };
    const nextParams = {
      pageNumber: 0,
      pageSize,
      searchParams: committedSearchParams,
    };
    patchStatusPagination({ pageNumber: 0, searchParams: committedSearchParams });
    searchGroupStatus(nextParams);
    getLatestDisaster();
  };

  return (
    <div className="space-y-4 py-2">
      <h3 className="text-xl font-bold">{t('eris.supportStatusList.title')}</h3>

      <div className="flex items-center gap-5">
        <div className="font-medium">{t('eris.supportStatusList.refinedSearch')}</div>

        <div className="flex flex-col gap-3">
          <FormRow label={t('eris.supportStatusList.buildingName')}>
            <input
              type="text"
              className="
                min-w-48 border-2 border-b-gray-300 border-l-gray-700 border-r-gray-300
                border-t-gray-700 bg-white px-1
                py-px text-sm
                text-black shadow-inner
              "
              value={inputBuildingName}
              onChange={handleOnBuildingNameChanged}
            />
          </FormRow>

          <FormRow label={t('eris.supportStatusList.target')}>
            <DropdownSelect
              initialOption={{ title: t('eris.csvParamsStatus.operationStatuses.all'), value: -1 }}
              selectedValue={
                inputOperationStatus !== undefined
                  ? {
                      title: resolveElevatorTitle(inputOperationStatus, t),
                      value: inputOperationStatus,
                    }
                  : undefined
              }
              optionValues={Object.values(ElevatorStatus)
                .filter((value) => typeof value === 'number')
                .map((value) => {
                  const numberValue = Number(value);
                  return { title: resolveElevatorTitle(numberValue, t), value: numberValue };
                })}
              onChange={handleOnTargetChanged}
            />
          </FormRow>
        </div>
      </div>

      <div className="flex justify-end">
        <Win95Button
          title={t('eris.supportStatusList.search')}
          onClick={handleOnSearch}
          disabled={false}
        />
      </div>

      <div className="py-2">
        <ErisResultsTable />
      </div>
    </div>
  );
};

export default ErisStatusResultsComponent;
